import { useState } from 'react'
import type { GeoJsonGeometry } from '../../types/geo'
import { useToast } from '../../hooks/useToast'

interface Props {
  value: GeoJsonGeometry | null
  onRedraw?: () => void
  onChange?: (geom: GeoJsonGeometry) => void
  readOnly?: boolean
}

export default function GeometryField({ value, onRedraw, onChange, readOnly }: Props) {
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState('')
  const [parseError, setParseError] = useState('')
  const { toast } = useToast()

  const json = value ? JSON.stringify(value, null, 2) : 'null'

  function startEdit() {
    setEditText(json)
    setParseError('')
    setEditing(true)
  }

  function commitEdit() {
    try {
      const parsed = JSON.parse(editText)
      if (!parsed || typeof parsed !== 'object' || !parsed.type || !parsed.coordinates) {
        throw new Error('Not a valid GeoJSON geometry')
      }
      onChange?.(parsed as GeoJsonGeometry)
      setEditing(false)
      setParseError('')
    } catch (err) {
      setParseError((err as Error).message)
    }
  }

  function copyJson() {
    navigator.clipboard.writeText(json)
    toast({ title: 'Copied GeoJSON' })
  }

  if (editing) {
    return (
      <div className="space-y-2">
        <textarea
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          className="w-full h-48 rounded-md border border-input bg-background p-3 text-xs font-mono resize-y focus:outline-none focus:ring-1 focus:ring-ring"
        />
        {parseError && <p className="text-xs text-destructive">{parseError}</p>}
        <div className="flex gap-2">
          <button
            onClick={commitEdit}
            className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground"
          >
            Apply
          </button>
          <button
            onClick={() => setEditing(false)}
            className="rounded bg-muted px-3 py-1 text-xs"
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <pre className="w-full max-h-48 overflow-auto rounded-md bg-muted/50 p-3 text-xs font-mono border border-border">
        {json}
      </pre>
      <div className="flex gap-2">
        <button
          onClick={copyJson}
          className="text-xs text-blue-400 hover:text-blue-300 underline underline-offset-2"
        >
          Copy JSON
        </button>
        {onRedraw && (
          <button
            onClick={onRedraw}
            className="text-xs text-blue-400 hover:text-blue-300 underline underline-offset-2"
          >
            Redraw
          </button>
        )}
        {!readOnly && onChange && (
          <button
            onClick={startEdit}
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
          >
            Edit GeoJSON
          </button>
        )}
      </div>
    </div>
  )
}
